/**
 * Settings > Account > Account removal (2026-10-29), as Discord's: Disable
 * account and Delete account. Each asks for the password and, with two-step
 * on, a code from the app or a recovery code. An owner of the server is
 * told to transfer it first (the server refuses with 40011). On success this
 * device signs out, and the sign-in screen says how to come back.
 */
import { ACCOUNT_DELETION_HOLD_DAYS, ApiErrorCode } from '@neosocial/protocol';
import { useState, type FormEvent } from 'react';
import { PasswordInput } from '../auth/PasswordInput';
import { useT } from '../i18n';
import { isApiRequestError } from '../lib/api';
import { failToast } from '../mod/actions';
import { SetGroup, SetRow } from '../settings/Settings';
import { useFlag } from '../store/config';
import { useData } from '../store/data';
import { Btn } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { removeAccount, type RemovalKind } from '../social/removal';

export function AccountRemoval() {
  const { t } = useT();
  const on = useFlag('account_removal');
  const [open, setOpen] = useState<RemovalKind | null>(null);
  if (!on) return null;
  return (
    <SetGroup title={t('nxs.rm.title')}>
      <SetRow label={t('nxs.rm.row')} sub={t('nxs.rm.sub')}>
        <span className="nxs-rm-btns">
          <Btn size="sm" kind="ghost" onClick={() => setOpen('disable')}>
            {t('nxs.rm.disable')}
          </Btn>
          <Btn size="sm" kind="danger" icon="trash" onClick={() => setOpen('delete')}>
            {t('nxs.rm.delete')}
          </Btn>
        </span>
      </SetRow>
      {open ? <RemovalDialog kind={open} onClose={() => setOpen(null)} /> : null}
    </SetGroup>
  );
}

function RemovalDialog({ kind, onClose }: { kind: RemovalKind; onClose(): void }) {
  const { t, fmt } = useT();
  const mfa = useData((d) => !!d.me?.mfa_enabled);
  // Discord's rule: the server's owner hands it over first (the server checks it too)
  // any server you own, the ones you made too (Console v2)
  const owned = useData((d) => {
    const g = Object.values(d.guilds).find((x) => !!d.me && x.owner_id === d.me.id);
    return g ? g.name : null;
  });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const title = t(kind === 'delete' ? 'nxs.rm.delete' : 'nxs.rm.disable');
  // when the deletion would be final, fixed when the dialog opens
  const [until] = useState(() => new Date(Date.now() + ACCOUNT_DELETION_HOLD_DAYS * 86_400_000));
  if (owned) {
    return (
      <Dialog
        title={t('nxs.rm.ownerTitle')}
        icon="crown"
        tone="mode"
        onClose={onClose}
        actions={
          <Btn kind="primary" size="sm" onClick={onClose}>
            {t('c.done')}
          </Btn>
        }
      >
        <p>{t('nxs.rm.owner', { server: owned })}</p>
      </Dialog>
    );
  }
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!password) return;
    if (mfa && !code.trim()) {
      setErr(t('nxs.rm.codeNeeded'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await removeAccount(kind, password, mfa ? code.replace(/\s/g, '') : undefined);
    } catch (er) {
      if (isApiRequestError(er, ApiErrorCode.INVALID_CREDENTIALS))
        setErr(t('nxs.rm.wrongPassword'));
      else if (isApiRequestError(er, ApiErrorCode.SECOND_FACTOR_REQUIRED))
        setErr(t('nxs.rm.codeNeeded'));
      else if (isApiRequestError(er, ApiErrorCode.INVALID_MFA_CODE)) setErr(t('nxs.rm.wrongCode'));
      else if (isApiRequestError(er, ApiErrorCode.MUST_TRANSFER_OWNERSHIP))
        setErr(t('ns.err.40011'));
      else failToast(er);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      icon={kind === 'delete' ? 'trash' : 'power'}
      tone="danger"
      onClose={onClose}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="danger"
            size="sm"
            busy={busy}
            disabled={!password || busy}
            onClick={() => void submit()}
          >
            {title}
          </Btn>
        </>
      }
    >
      <p>
        {kind === 'delete'
          ? t('nxs.rm.deleteBody', { date: fmt.date(until) })
          : t('nxs.rm.disableBody')}
      </p>
      <form className="nx-set-form" onSubmit={(e) => void submit(e)}>
        <label className="ns-field">
          <span className="ns-label">{t('nxs.rm.password')}</span>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            data-autofocus
            onChange={(e) => (setPassword(e.target.value), setErr(null))}
            aria-invalid={err === t('nxs.rm.wrongPassword') || undefined}
          />
        </label>
        {mfa ? (
          <label className="ns-field">
            <span className="ns-label">{t('nxs.rm.code')}</span>
            <input
              className="nx-input ns-code-in"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              spellCheck={false}
              maxLength={12}
              value={code}
              onChange={(e) => (setCode(e.target.value), setErr(null))}
            />
            <span className="ns-help">{t('nxs.rm.codeHelp')}</span>
          </label>
        ) : null}
        {err ? (
          <p className="ns-err" role="alert">
            {err}
          </p>
        ) : null}
        {/* Enter submits */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}
